import React, { useState, useEffect, useCallback } from "react";
import Paper from "@material-ui/core/Paper";
import { useRouteMatch, useLocation } from "react-router-dom";
import { makeStyles, Theme, createStyles } from "@material-ui/core";
import TabsComponent from "./common/Tabs";
import { PERFORMANCE_NAVIGATION_TABS } from "../utils/Constants";
import { useQuery } from "hooks/useQueryParams";
import { useSelector } from "react-redux";
import {
  selectCallerAccountId,
  selectIsKPIExecutiveSummaryFeatureEnabled,
} from "store/app-config/AppConfigSelectors";
import {
  selectPerformanceReportDateRange,
  selectPerformanceReportStoreId,
  selectIsReportAPIAvailable,
  selectKPIAnalysisNotFound,
  selectShouldDisableSummaryTab,
} from "store/performance-report/PerformanceReportSelectors";
import { useHasEntitlement } from "hooks/useEntitlement";
import {
  selectKpiCategories,
  selectKpiConfigIsLoaded,
} from "store/kpi-config/KpiConfigSelectors";
import {
  getTabIndexFromPath,
  buildPerformanceRoutes,
  PerformanceRoute,
} from "utils/KpiConfigUtils";

interface PerformanceNavigationTabsProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabs: {
      minHeight: "35px",
      backgroundColor: "#F2F3F4",
      "& .MuiTab-root": {
        padding: 0,
        minHeight: "35px",
      },
    },
    indicator: {
      display: "flex",
      justifyContent: "center",
      backgroundColor: "transparent",
      color: "black",
      "& > span": {
        maxWidth: "75%",
        width: "100%",
        backgroundColor: "rgb(112, 155, 177)",
      },
    },
  }),
);

const PerformanceNavigationTabs = (props: PerformanceNavigationTabsProps) => {
  const classes = useStyles();
  const { url } = useRouteMatch();
  const location = useLocation();
  const accountIdQuery = useQuery().get("accountId") ?? "";
  let userAccountId = useSelector(selectCallerAccountId) || accountIdQuery;
  const selectedStore = useSelector(selectPerformanceReportStoreId);
  const selectedDateRange = useSelector(selectPerformanceReportDateRange);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const isAnalysisNotFound = useSelector(selectKPIAnalysisNotFound);
  const shouldDisableSummaryTab = useSelector(selectShouldDisableSummaryTab);
  const isKPIExecutiveSummaryFeatureEnabled = useSelector(
    selectIsKPIExecutiveSummaryFeatureEnabled,
  );
  const hasAiEntitlement = useHasEntitlement("ai");
  const kpiCategories = useSelector(selectKpiCategories);
  const isKpiConfigLoaded = useSelector(selectKpiConfigIsLoaded);
  const getInitialValue = useCallback(() => {
    const path = location.pathname;
    const dynamicIndex = getTabIndexFromPath(
      path,
      kpiCategories,
      isKpiConfigLoaded,
    );
    if (dynamicIndex !== null) return dynamicIndex;
    if (
      isKpiConfigLoaded &&
      kpiCategories.length > 0 &&
      path.includes(PERFORMANCE_NAVIGATION_TABS.EXECUTIVE_SUMMARY) &&
      isKPIExecutiveSummaryFeatureEnabled &&
      hasAiEntitlement
    ) {
      return kpiCategories.length;
    }

    if (path.includes(PERFORMANCE_NAVIGATION_TABS.TRAFFIC)) return 0;
    if (path.includes(PERFORMANCE_NAVIGATION_TABS.PRODUCT)) return 1;
    if (path.includes(PERFORMANCE_NAVIGATION_TABS.CONVERSION)) return 2;
    if (path.includes(PERFORMANCE_NAVIGATION_TABS.REVENUE)) return 3;
    if (
      path.includes(PERFORMANCE_NAVIGATION_TABS.EXECUTIVE_SUMMARY) &&
      isKPIExecutiveSummaryFeatureEnabled &&
      hasAiEntitlement
    )
      return 4;
    return 0;
  }, [
    location.pathname,
    isKPIExecutiveSummaryFeatureEnabled,
    hasAiEntitlement,
    isKpiConfigLoaded,
    kpiCategories,
  ]);

  const [value, setValue] = useState<number>(getInitialValue());

  const disableSubNavTabs =
    selectedStore === null || selectedDateRange === "" || !isReportAPIAvailable;

  const routes = React.useMemo(() => {
    let dynamicRoutes: PerformanceRoute[] = buildPerformanceRoutes(
      kpiCategories,
      isKpiConfigLoaded,
      url,
      disableSubNavTabs,
    );

    if (dynamicRoutes.length === 0) {
      dynamicRoutes = [
        {
          name: {
            id: "performanceNavigationTabs.traffic",
            defaultMessage: "Traffic",
          },
          link: `${url}/${PERFORMANCE_NAVIGATION_TABS.TRAFFIC}`,
          disabled: disableSubNavTabs,
          activeIndex: 0,
        },
        {
          name: {
            id: "performanceNavigationTabs.product",
            defaultMessage: "Product",
          },
          link: `${url}/${PERFORMANCE_NAVIGATION_TABS.PRODUCT}`,
          disabled: disableSubNavTabs,
          activeIndex: 1,
        },
        {
          name: {
            id: "performanceNavigationTabs.conversion",
            defaultMessage: "Conversion",
          },
          link: `${url}/${PERFORMANCE_NAVIGATION_TABS.CONVERSION}`,
          disabled: disableSubNavTabs,
          activeIndex: 2,
        },
        {
          name: {
            id: "performanceNavigationTabs.revenue",
            defaultMessage: "Revenue",
          },
          link: `${url}/${PERFORMANCE_NAVIGATION_TABS.REVENUE}`,
          disabled: disableSubNavTabs,
          activeIndex: 3,
        },
      ];
    }

    if (isKPIExecutiveSummaryFeatureEnabled && hasAiEntitlement) {
      dynamicRoutes.push({
        name: {
          id: "performanceNavigationTabs.summary",
          defaultMessage: "Analysis",
        },
        link: `${url}/${PERFORMANCE_NAVIGATION_TABS.EXECUTIVE_SUMMARY}`,
        disabled: shouldDisableSummaryTab,
        activeIndex: dynamicRoutes.length,
      });
    }

    return dynamicRoutes;
  }, [
    url,
    disableSubNavTabs,
    shouldDisableSummaryTab,
    isKPIExecutiveSummaryFeatureEnabled,
    hasAiEntitlement,
    isKpiConfigLoaded,
    kpiCategories,
  ]);

  useEffect(() => {
    const newValue = getInitialValue();
    if (newValue !== value) {
      setValue(newValue);
    }
  }, [location.pathname, getInitialValue, value]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  // Determine tooltip message based on conditions
  const getTooltipMessage = (isSummaryTab: boolean = false) => {
    if (selectedStore === null || selectedDateRange === "") {
      return isSummaryTab
        ? "performanceNavigationTabs.summarySelectStoreAndDateRange"
        : "performanceNavigationTabs.selectStoreAndDateRange";
    }

    if (
      isSummaryTab &&
      (shouldDisableSummaryTab || isAnalysisNotFound || !isReportAPIAvailable)
    ) {
      return "performanceNavigationTabs.summaryKpiNotAvailable";
    } else if (!isReportAPIAvailable) {
      return "performanceNavigationTabs.kpiNotAvailable";
    }
    return undefined;
  };

  // Add tooltip messages to routes that should be disabled
  const routesWithTooltips = routes.map((route) => {
    const isSummaryRoute = route.activeIndex === 4;
    const shouldShowTooltip = isSummaryRoute
      ? shouldDisableSummaryTab
      : disableSubNavTabs || route.disabled;
    return {
      ...route,
      tooltipMessage: shouldShowTooltip
        ? getTooltipMessage(isSummaryRoute)
        : undefined,
    };
  });

  return (
    <Paper variant="elevation" elevation={0}>
      <TabsComponent
        tabsId="PerformanceNavigationTabs"
        classes={classes}
        value={value}
        handleChange={handleChange}
        routes={routesWithTooltips}
        userAccountId={userAccountId}
        disableSubNavTabs={disableSubNavTabs}
      />
    </Paper>
  );
};

export default PerformanceNavigationTabs;
